// GateNotes.tsx — (TF-6b-4; design §4 "gate notes shown nowhere", planning 10-17 11:25) the engine's notes for the gate a run
// waits at. Admin (the Run Inspector, beside the step): the gate in words, each check's status and grade, its reasons when
// the engine's gate-notes read answers ("the reasons aren't readable yet" until then). (tf3-38) C2.0's rules card is shown,
// and edited, by RulesCardPanel beside it. Customer (the video page): "Why the engine stopped here", the grade's words only.
// The screenplay, read-only.
import { useEffect, useState } from "react";
import { apiGet } from "@/api/client.ts";
import { checkLine, noteLines, REASONS_NOT_READABLE, screenplayLines, type GateNotesView, type ScreenplayView } from "@/lib/gate-notes.ts";
import { w } from "@/lib/words.ts";   // (B107) the customer's line

export function AdminGateNotes({ runId, stepId }: { runId: string; stepId: string }) {
  const [n, setN] = useState<GateNotesView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    // (audit 10-20 B4) a new gate starts clean, and an answer that lands after a newer one is dropped
    let alive = true;
    setErr(null); setN(null);
    apiGet<GateNotesView>(`/admin/runs/${runId}/gate-notes`).then((r) => { if (alive) setN(r); })
      .catch((e) => { if (alive) setErr(String((e as Error)?.message ?? e)); });   // catch-reason: shown in place of the notes
    return () => { alive = false; };
  }, [runId, stepId]);
  if (err) return <div style={{ color: "#b91c1c", fontSize: 13 }}>The gate's notes couldn't be read: {err}</div>;
  if (!n?.gate) return null;
  return (
    <div style={{ fontSize: 13, margin: "8px 0" }}>
      <div><strong>Why the engine stopped here:</strong> {n.gate.holdsAt ?? n.gate.name}</div>
      <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{n.checks.map((c) => (
        <li key={`${c.stepId}:${c.beatId ?? ""}`} style={c.passed === false ? { color: "#b91c1c" } : undefined}>{checkLine(c)}
          {c.reasons?.length ? <ul>{c.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul> : null}</li>))}</ul>
      {n.reasons === "not_readable" && <div className="tf-muted">{REASONS_NOT_READABLE}</div>}
      {noteLines(n.gate.note).length > 0 && <div style={{ marginTop: 4 }}><strong>The gate's note</strong>
        <ul style={{ margin: "2px 0 0 18px", fontSize: 12 }}>{noteLines(n.gate.note).map((l, i) => <li key={i}>{l}</li>)}</ul></div>}
      {n.gate.onReject && <div className="tf-muted">If rejected: {n.gate.onReject}</div>}
    </div>
  );
}

export function AdminScreenplay({ runId }: { runId: string }) {
  const [s, setS] = useState<ScreenplayView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    apiGet<ScreenplayView>(`/admin/runs/${runId}/screenplay`).then(setS).catch((e) => setErr(String((e as Error)?.message ?? e)));   // catch-reason: shown in place of the words
  }, [runId]);
  if (err) return <div style={{ color: "#b91c1c", fontSize: 13 }}>The screenplay couldn't be read: {err}</div>;
  if (!s) return <div className="tf-muted">Reading the screenplay…</div>;
  const lines = screenplayLines(s);
  return (
    <div style={{ fontSize: 13 }}>
      <strong>The screenplay (read-only)</strong>
      {lines.kind === "lines" ? <ol style={{ margin: "4px 0", paddingLeft: 18 }}>{lines.lines.map((l, i) => <li key={i}>{l}</li>)}</ol>
        : <div style={{ color: "#92400e", marginTop: 4 }}>{lines.text}</div>}
    </div>
  );
}

export function WhyStopped({ videoId }: { videoId: string }) {
  const [words, setW] = useState<string | null>(null);
  useEffect(() => {
    apiGet<{ words: string | null }>(`/videos/${videoId}/gate-notes`).then((r) => setW(r.words)).catch(() => setW(null));   // catch-reason: the line is left out; the gate's own screen still works
  }, [videoId]);
  return words ? <p className="tf-muted" style={{ fontSize: 13 }}><strong>{w("page.why_stopped")}</strong> {words}</p> : null;
}
